import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    FlexBox,
    Link,
    SkeletonText,
    SkeletonTextLine,
    Spinner,
    Typography,
} from '@vp/swan';
import { useLogger, useUserContext } from '@vp/ubik-context';
import { sanitiseLocale } from '../../i18n/config';
import { BusinessName } from '../../pages/BusinessNameGeneratorPage/components/BusinessNameResults/BusinessNameResults';
import { useTrack, useTrackNavigationClicked } from '../../utils/analytics';
import { useEnvContext } from '../../utils/environmentContext';
import { formatDomainName } from '../../utils/formatDomainName';
import { removePathQueryParams } from '../../utils/removePathQueryParams';
import type { Logger } from '@vp/ubik-logging';

interface DomainNamesProps {
    trackingEventDetails: {
        category: string;
    };
    businessName: BusinessName;
}

interface DomainSuggestions {
    domain: string;
}

interface DomainResponse {
    domain: string;
    available: boolean;
    suggestions?: DomainSuggestions[];
}

const DomainNames = ({ trackingEventDetails, businessName }: DomainNamesProps) => {
    const [domainList, setDomainList] = useState<DomainSuggestions[] | undefined>();
    const [isDotComAvailable, setIsDotComAvailable] = useState<boolean>(false);
    const [isApiError, setIsApiError] = useState<boolean>(false);
    const track = useTrack();
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const logger: Logger = useLogger();
    const { ENV_DOMAIN_SEARCH_URL } = useEnvContext();
    const domainNameDotCom = `${formatDomainName(businessName.name)}.com`;
    const pathName = useUserContext().forwardedPath || '';
    const currentPagePath = removePathQueryParams(pathName);
    const destinationPagePath = '/digital-marketing/domains/search';

    const fetchDomainNames = async () => {
        try {
            const apiURL = `${ENV_DOMAIN_SEARCH_URL}?locale=${locale}`;
            const response = await fetch(apiURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ domain: domainNameDotCom }),
            });
            if (!response.ok) {
                logger.error({
                    name: 'BNG: Failed request to fetch-domain-names',
                    message: `Request to ${apiURL} failed to fetch domain names. Status: ${response.status}.`,
                    logData: {
                        businessName: businessName.name,
                        domain: domainNameDotCom,
                        response: JSON.stringify(response.body),
                    },
                });
                setIsApiError(true);
                return;
            }
            const data: DomainResponse = await response.json();
            setIsDotComAvailable(data.available);
            if (data.available) {
                setDomainList([{ domain: data.domain }]);
            } else {
                setDomainList(data.suggestions);
            }
        } catch (err) {
            logger.error({
                name: 'BNG: Error fetching domain names',
                message: (err as Error).message,
                logData: {
                    businessName: businessName.name,
                    domain: domainNameDotCom,
                },
            });
            setIsApiError(true);
        }
    };

    useEffect(() => {
        fetchDomainNames();
    }, []);

    const checkDomainsCtaRef = useTrackNavigationClicked(
        trackingEventDetails.category,
        'BusinessNameDetailsModal',
        destinationPagePath,
        'checkDomainAvailability',
    );

    // Setting 'wix-domains-searched-domain' in localStorage to pre-populate the domain search field on /digital-marketing/domains/search.
    // Pre-populating the domain search field won't work in development or feature branches as localStorage only works across pages on the same origin & the digital-marketing/domains/search link returns a 404.
    // For example, localhost -> localhost works, but localhost:3000/business-name-generator -> vistaprint.com/digital-marketing/domains/search won't work.
    const handleDomainSearchClick = (selectedDomainName: string) => {
        const domainSearchStorageKey = 'wix-domains-searched-domain';
        track('Navigation Clicked', {
            pageSection: 'BusinessNameDetailsModal',
            category: trackingEventDetails.category,
            label: 'Content:BusinessNameDetailsModal',
            eventDetail: `${currentPagePath};${destinationPagePath};BusinessNameDetailsModal;getItButton`,
            navigationDetail: 'getItButton',
            selectedDomainName,
        });
        try {
            localStorage.setItem(domainSearchStorageKey, `"${selectedDomainName}"`);
        } catch (e) {
            logger.error({
                name: `BNG: Failed to set ${domainSearchStorageKey} as "${selectedDomainName}" in localStorage`,
                message: (e as Error).message,
            });
        }
    };

    return (
        <Box marginY="between-subsections">
            {isApiError ? (
                <Button
                    skin="primary"
                    render={(props) => (
                        <Link
                            href={destinationPagePath}
                            target="_blank"
                            className={props.className}
                            ref={checkDomainsCtaRef}
                            onClick={() => handleDomainSearchClick(domainNameDotCom)}
                            rel="noreferrer"
                        >
                            {props.children}
                        </Link>
                    )}
                >
                    Check domain availability
                </Button>
            ) : domainList ? (
                <>
                    <Typography fontSkin="title-subsection" marginBottom="3">
                        {isDotComAvailable
                            ? 'Yay! Your domain is available'
                            : `It looks like ${domainNameDotCom} is taken but don’t stress! Here are some more options`}
                    </Typography>
                    <FlexBox flexDirection="column" gap="between-actions">
                        {domainList.map((currentDomain) => (
                            <FlexBox
                                justifyContent="space-between"
                                alignItems="center"
                                key={currentDomain.domain}
                            >
                                <Typography fontSkin="body-standard">
                                    {currentDomain.domain}
                                </Typography>
                                <Link
                                    href={destinationPagePath}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => handleDomainSearchClick(currentDomain.domain)}
                                >
                                    Get it
                                </Link>
                            </FlexBox>
                        ))}
                    </FlexBox>
                    <Button
                        skin="secondary"
                        marginTop="to-actions"
                        render={(props) => (
                            <Link
                                href={destinationPagePath}
                                target="_blank"
                                className={props.className}
                                ref={checkDomainsCtaRef}
                                onClick={() => handleDomainSearchClick(domainNameDotCom)}
                                rel="noreferrer"
                            >
                                {props.children}
                            </Link>
                        )}
                    >
                        Explore More Domains
                    </Button>
                </>
            ) : (
                <>
                    <Spinner
                        accessibleText="Checking Domains…"
                        layout="horizontal"
                        showText
                        fontWeight="bold"
                        marginBottom="between-subsections"
                    />
                    <SkeletonText>
                        <SkeletonTextLine width="narrow" />
                        <SkeletonTextLine />
                        <SkeletonTextLine />
                    </SkeletonText>
                </>
            )}
        </Box>
    );
};

export default DomainNames;
